import { FC, Fragment, ReactElement, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatPrice } from '@mopla/utils';

type TRender = (parts: {
	priceItems: string[];
	previousPrice?: ReactNode;
	separator?: ReactNode;
}) => ReactElement;

export interface IPriceStringProps {
	price?: number | null;
	priceRangePerPerson?: [number, number] | null;
	hasPriceTariff?: boolean | null;
	/** the ride costs nothing — rendered as its own wording, not as 0,00 € */
	isFree?: boolean | null;
	/** if set, then either the price|priceRangePerPerson or hasPriceTariff will be shown */
	single?: boolean;
	separator?: ReactNode;
	previousPrice?: number;
	render?: TRender;
	i18nVersion?: 'V2';
}

const renderPriceString: TRender = ({ priceItems, separator }) => {
	return (
		<>
			{priceItems.map((item, i, s) => (
				<Fragment key={i}>
					{item}
					{!!s[i + 1] && separator}
				</Fragment>
			))}
		</>
	);
};

export const PriceString: FC<IPriceStringProps> = ({
	priceRangePerPerson,
	price,
	hasPriceTariff,
	isFree,
	single,
	separator = ' + ',
	previousPrice,
	render = renderPriceString,
	i18nVersion,
}) => {
	const { t } = useTranslation();

	const priceItems = useMemo(() => {
		if (!priceRangePerPerson && price == null && !hasPriceTariff && !isFree) {
			return null;
		}
		const priceAccordingToTariff =
			hasPriceTariff &&
			t('booking:text.price_according_to_tariff', { context: i18nVersion });

		const freeRide = isFree && t('booking:text.free_ride');

		const formattedPrice = priceRangePerPerson
			? formatPriceRange(...priceRangePerPerson)
			: price != null
			? formatPrice(price / 100)
			: null;

		/** If single, pick either priceAccordingToTariff (1st priority) or freeRide or formattedPrice or null */
		if (single) {
			return priceAccordingToTariff
				? [priceAccordingToTariff]
				: freeRide
				? [freeRide]
				: formattedPrice
				? [formattedPrice]
				: null;
		}

		return [formattedPrice, freeRide, priceAccordingToTariff].filter(
			(v): v is string => !!v
		);
	}, [
		hasPriceTariff,
		i18nVersion,
		isFree,
		price,
		priceRangePerPerson,
		single,
		t,
	]);

	if (!priceItems) {
		return null;
	}

	return render({
		priceItems,
		separator,
		previousPrice:
			previousPrice != null ? formatPrice(previousPrice / 100) : undefined,
	});
};

function formatPriceRange(min: number, max: number) {
	return min === max
		? `${formatPrice(max / 100)}`
		: `${formatPrice(min / 100, false)} - ${formatPrice(max / 100)}`;
}
